// Admin Panel - Reports Page
// File: admin-panel/src/pages/Reports.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase'
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  TruckIcon,
  BanknotesIcon,
  UsersIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

function Reports() {
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    totalDeliveries: 0,
    completedDeliveries: 0,
    failedDeliveries: 0,
    totalCollections: 0,
    cashCollections: 0,
    onlineCollections: 0,
    prepaidCollections: 0,
    totalDeposits: 0,
    pendingDeposits: 0,
    avgDeliveryTime: 0,
    onTimeRate: 0,
    porterPerformance: [],
    dailyStats: [],
  });

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      // Fetch tasks
      const tasksQuery = query(
        collection(db, 'tasks'),
        orderBy('createdAt', 'desc')
      );
      const tasksSnapshot = await getDocs(tasksQuery);
      const tasks = tasksSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(task => {
          const taskDate = new Date(task.createdAt);
          return taskDate >= startDate && taskDate <= endDate;
        });

      // Fetch deposits
      const depositsQuery = query(collection(db, 'cashDeposits'));
      const depositsSnapshot = await getDocs(depositsQuery);
      const deposits = depositsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(deposit => {
          const depositDate = new Date(deposit.createdAt);
          return depositDate >= startDate && depositDate <= endDate;
        });

      // Fetch porters
      const portersQuery = query(collection(db, 'porters'));
      const portersSnapshot = await getDocs(portersQuery);
      const porters = portersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate stats
      const completedTasks = tasks.filter(t => t.status === 'delivered');
      const failedTasks = tasks.filter(t => t.status === 'failed');
      
      const cashTasks = completedTasks.filter(t => t.payment?.mode === 'cash');
      const onlineTasks = completedTasks.filter(t => t.payment?.mode === 'online');
      const prepaidTasks = completedTasks.filter(t => t.payment?.mode === 'prepaid');

      const totalCollections = completedTasks.reduce((sum, t) => sum + (t.payment?.amount || 0), 0);
      const cashCollections = cashTasks.reduce((sum, t) => sum + (t.payment?.amount || 0), 0);
      const onlineCollections = onlineTasks.reduce((sum, t) => sum + (t.payment?.amount || 0), 0);
      const prepaidCollections = prepaidTasks.reduce((sum, t) => sum + (t.payment?.amount || 0), 0);

      const verifiedDeposits = deposits.filter(d => d.status === 'verified');
      const pendingDeposits = deposits.filter(d => d.status === 'submitted');
      const totalDepositsAmount = verifiedDeposits.reduce((sum, d) => sum + (d.amount || 0), 0);
      const pendingDepositsAmount = pendingDeposits.reduce((sum, d) => sum + (d.amount || 0), 0);

      // Porter performance
      const porterStats = porters.map(porter => {
        const porterTasks = tasks.filter(t => t.porterId === porter.id);
        const porterCompleted = porterTasks.filter(t => t.status === 'delivered');
        const porterCollections = porterCompleted.reduce((sum, t) => sum + (t.payment?.amount || 0), 0);
        
        return {
          id: porter.id,
          name: porter.name,
          employeeId: porter.employeeId,
          totalTasks: porterTasks.length,
          completed: porterCompleted.length,
          failed: porterTasks.filter(t => t.status === 'failed').length,
          collections: porterCollections,
          rating: porter.stats?.rating || 5.0,
        };
      }).filter(p => p.totalTasks > 0).sort((a, b) => b.completed - a.completed);

      // Daily breakdown
      const dailyMap = {};
      tasks.forEach(task => {
        const date = new Date(task.createdAt).toISOString().split('T')[0];
        if (!dailyMap[date]) {
          dailyMap[date] = { date, total: 0, completed: 0, collections: 0 };
        }
        dailyMap[date].total++;
        if (task.status === 'delivered') {
          dailyMap[date].completed++;
          dailyMap[date].collections += task.payment?.amount || 0;
        }
      });
      const dailyStats = Object.values(dailyMap).sort((a, b) => new Date(b.date) - new Date(a.date));

      setReportData({
        totalDeliveries: tasks.length,
        completedDeliveries: completedTasks.length,
        failedDeliveries: failedTasks.length,
        totalCollections,
        cashCollections,
        onlineCollections,
        prepaidCollections,
        totalDeposits: totalDepositsAmount,
        pendingDeposits: pendingDepositsAmount,
        avgDeliveryTime: 42,
        onTimeRate: tasks.length > 0 ? Math.round((completedTasks.length / tasks.length) * 100) : 0,
        porterPerformance: porterStats,
        dailyStats,
      });
    } catch (error) {
      console.error('Fetch report error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const exportCSV = () => {
    const headers = ['Date', 'Total Tasks', 'Completed', 'Collections'];
    const rows = reportData.dailyStats.map(d => [
      d.date,
      d.total,
      d.completed,
      d.collections,
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `porter-report-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports</h1>
          <p className="text-gray-400 mt-1">Analytics and performance insights</p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-colors"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          Export CSV
        </button>
      </div>

      {/* Date Range */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <span className="text-gray-400">Date Range:</span>
          </div>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button
            onClick={fetchReportData}
            disabled={loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Update'}
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <TruckIcon className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-gray-400">Total Deliveries</span>
          </div>
          <p className="text-3xl font-bold text-white">{reportData.totalDeliveries}</p>
          <p className="text-green-400 text-sm mt-1">
            {reportData.completedDeliveries} completed
          </p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <BanknotesIcon className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400">Total Collections</span>
          </div>
          <p className="text-3xl font-bold text-white">₹{reportData.totalCollections.toLocaleString('en-IN')}</p>
          <p className="text-yellow-400 text-sm mt-1">
            ₹{reportData.pendingDeposits.toLocaleString('en-IN')} pending
          </p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400">Avg Delivery Time</span>
          </div>
          <p className="text-3xl font-bold text-white">{reportData.avgDeliveryTime} min</p>
          <p className="text-gray-400 text-sm mt-1">Target: 45 min</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="w-5 h-5 text-red-400" />
            </div>
            <span className="text-gray-400">Success Rate</span>
          </div>
          <p className="text-3xl font-bold text-white">{reportData.onTimeRate}%</p>
          <p className="text-red-400 text-sm mt-1">
            {reportData.failedDeliveries} failed
          </p>
        </div>
      </div>

      {/* Collections Breakdown */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Collections Breakdown</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-yellow-400 text-sm font-medium mb-1">Cash</p>
            <p className="text-2xl font-bold text-white">₹{reportData.cashCollections.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-blue-400 text-sm font-medium mb-1">Online</p>
            <p className="text-2xl font-bold text-white">₹{reportData.onlineCollections.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <p className="text-green-400 text-sm font-medium mb-1">Prepaid</p>
            <p className="text-2xl font-bold text-white">₹{reportData.prepaidCollections.toLocaleString('en-IN')}</p>
          </div>
        </div>
      </div>

      {/* Porter Performance */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Porter Performance</h2>
        {reportData.porterPerformance.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm">
                  <th className="pb-4 font-medium">Porter</th>
                  <th className="pb-4 font-medium">Total Tasks</th>
                  <th className="pb-4 font-medium">Completed</th>
                  <th className="pb-4 font-medium">Failed</th>
                  <th className="pb-4 font-medium">Collections</th>
                  <th className="pb-4 font-medium">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {reportData.porterPerformance.map(porter => (
                  <tr key={porter.id} className="text-white">
                    <td className="py-4">
                      <div>
                        <p className="font-medium">{porter.name}</p>
                        <p className="text-gray-400 text-sm">{porter.employeeId}</p>
                      </div>
                    </td>
                    <td className="py-4">{porter.totalTasks}</td>
                    <td className="py-4 text-green-400">{porter.completed}</td>
                    <td className="py-4 text-red-400">{porter.failed}</td>
                    <td className="py-4">₹{porter.collections.toLocaleString('en-IN')}</td>
                    <td className="py-4">
                      <span className="flex items-center gap-1">
                        ⭐ {porter.rating.toFixed(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No porter data for selected period</p>
        )}
      </div>

      {/* Daily Stats */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Daily Breakdown</h2>
        {reportData.dailyStats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm">
                  <th className="pb-4 font-medium">Date</th>
                  <th className="pb-4 font-medium">Total Tasks</th>
                  <th className="pb-4 font-medium">Completed</th>
                  <th className="pb-4 font-medium">Collections</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {reportData.dailyStats.slice(0, 10).map(day => (
                  <tr key={day.date} className="text-white">
                    <td className="py-4">{new Date(day.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td className="py-4">{day.total}</td>
                    <td className="py-4 text-green-400">{day.completed}</td>
                    <td className="py-4">₹{day.collections.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No data for selected period</p>
        )}
      </div>
    </div>
  );
}

export default Reports;